import {useMemo, useRef} from 'react';
import {Dock} from '../dock/Dock';
import {DockItem} from '../dock/DockItem';
import {DockSeparator} from '../dock/DockSeparator';
import {APP_BY_ID} from '../data/apps';
import {AppIcon} from '../shell/AppIcon';
import {useDesktop} from './DesktopContext';
import type {WindowState} from '../types';

/** How far a parking slot may stray from a square, in either direction. */
const MIN_ASPECT = 0.8;
const MAX_ASPECT = 2.2;

/**
 * A minimised window, parked beside the app icons.
 *
 * The thumbnail is not a screenshot — it is the app itself, rendered a second
 * time at window size and scaled down with one transform. That costs no
 * rasterising library, stays live (a deal closed in another window shows up
 * here), and it is the only version that cannot go stale.
 *
 * The slot is cut to the window's own aspect ratio, so the window folding into
 * it does not have to distort to fit, and it starts hidden (`data-flying`,
 * cleared by the motion layer) so the thumbnail appears as the window lands
 * rather than before it has left.
 */
function MinimizedSlot({state, size}: {state: WindowState; size: number}) {
  const {restoreWindow} = useDesktop();
  const hostRef = useRef<HTMLDivElement>(null);
  const app = APP_BY_ID[state.appId];
  if (!app) return null;

  const aspect = Math.min(Math.max(state.width / state.height, MIN_ASPECT), MAX_ASPECT);
  const width = Math.round(size * aspect);
  const scale = width / state.width;
  const View = app.view;

  return (
    <DockItem
      id={`slot-${state.id}`}
      label={app.name}
      width={width}
      height={size}
      onActivate={() => restoreWindow(state.id, hostRef.current?.getBoundingClientRect())}
    >
      {/* The badge lives INSIDE the slot, not in the dock's indicator layer, so
          that hiding the slot during the flight hides the badge with it — a
          lone icon floating over an empty gap reads as a glitch. */}
      <div ref={hostRef} data-minimized-id={state.id} className="dock-slot relative h-full w-full">
        <div className="h-full w-full overflow-hidden rounded-[inherit] bg-canvas">
          <div
            className="pointer-events-none flex flex-col"
            style={{
              height: `${state.height}px`,
              transform: `scale(${scale})`,
              transformOrigin: '0 0',
              width: `${state.width}px`,
            }}
          >
            <div className="bar flex h-10 shrink-0 items-center px-4">
              <p className="min-w-0 flex-1 truncate text-[12.5px] font-bold tracking-tight text-ink">
                {app.name}
              </p>
            </div>
            <div className="min-h-0 flex-1 overflow-hidden">
              <View />
            </div>
          </div>
        </div>

        {/* Which app this is, at a glance: the thumbnail alone is unreadable at
            this size, and reading it is not the point — recognising it is. */}
        <span className="pointer-events-none absolute -bottom-1 -right-1 h-[42%] w-[42%]">
          <AppIcon app={app} />
        </span>
      </div>
    </DockItem>
  );
}

/**
 * The dock, reduced to what a work tool needs: launch, show what is running,
 * park, reorder. No trash and no drag-out-to-delete — but minimised windows do
 * get their own parking slots, because "where did my window go" is a question
 * a work tool should never make anyone ask.
 */
export function DesktopDock() {
  const {config, dockOrder, heldSlots, launching, openApp, setDockOrder, windows} = useDesktop();

  const state = useMemo(() => {
    const map = new Map<string, {minimized: boolean}>();
    windows.forEach((win) => map.set(win.appId, {minimized: win.minimized}));
    return map;
  }, [windows]);

  // A slot also stays up for a window that is no longer minimised but is still
  // pouring back out of it — the dock must not close the gap under the animation.
  const minimized = useMemo(
    () => windows.filter((win) => win.minimized || heldSlots.includes(win.id)),
    [heldSlots, windows],
  );

  return (
    <div className="fixed bottom-3 left-1/2 z-[8000] -translate-x-1/2">
      <Dock
        {...config}
        className="dock-shell"
        onSort={(detail) => setDockOrder(detail.ids)}
      >
        {dockOrder.map((appId) => {
          const app = APP_BY_ID[appId];
          if (!app) return null;
          const running = state.get(app.id);
          return (
            <DockItem
              key={app.id}
              id={app.id}
              label={app.name}
              bouncing={launching === app.id}
              onActivate={() => openApp(app.id)}
              indicator={
                running ? (
                  <span
                    className={`absolute -bottom-[7px] left-1/2 h-[4px] w-[4px] -translate-x-1/2 rounded-full ${
                      running.minimized ? 'bg-ink-3' : 'bg-accent'
                    }`}
                  />
                ) : null
              }
            >
              <AppIcon app={app} />
            </DockItem>
          );
        })}

        {/* A separator is also a reorder boundary: parked windows cannot be
            dragged into the app strip, and app icons cannot be dropped here. */}
        {minimized.length > 0 ? (
          <>
            <DockSeparator />
            {minimized.map((win) => (
              <MinimizedSlot key={win.id} state={win} size={config.size} />
            ))}
          </>
        ) : null}
      </Dock>
    </div>
  );
}
